import React from 'react'
import {FormikProps} from 'formik'
import {IntlShape} from 'react-intl'
import TagsComponent from '../../../../_dashboard/partials/widgets/tags/TagsComponent'

type HotelTranslation = {
  _id?: string
  language?: string
  metaTitle?: string
  metaDescription?: string
  metaKeywords?: string[]
  openGraphTitle?: string
  openGraphDescription?: string
  openGraphImage?: string
  openGraphType?: string
  twitterTitle?: string
  twitterDescription?: string
  twitterImage?: string
}

type HotelSeoFormValues = {
  seoSlug?: string
  translations?: HotelTranslation[]
}

/** Minimal Formik surface — compatible with the full hotel edit form in HotelDetails. */
type HotelSeoFormik = Pick<
  FormikProps<HotelSeoFormValues>,
  'values' | 'setFieldValue' | 'handleChange' | 'handleBlur'
>

interface HotelSeoTabProps {
  formik: HotelSeoFormik
  intl: IntlShape
  selectedLanguage: string
}

const HotelSeoTab: React.FC<HotelSeoTabProps> = ({formik, intl, selectedLanguage}) => {
  const normLang = (l: unknown) => String(l ?? '').trim().toLowerCase()
  const lc = normLang(selectedLanguage)

  const emptySeoTranslation = (language: string): HotelTranslation => ({
    language,
    metaTitle: '',
    metaDescription: '',
    metaKeywords: [],
    openGraphTitle: '',
    openGraphDescription: '',
    openGraphImage: '',
    openGraphType: 'website',
    twitterTitle: '',
    twitterDescription: '',
    twitterImage: '',
  })

  const getCurrentTranslation = (): HotelTranslation => {
    const translations = formik.values.translations || []
    return (
      translations.find((t) => normLang(t.language) === lc) ||
      emptySeoTranslation(selectedLanguage)
    )
  }

  const updateTranslation = (field: keyof HotelTranslation, value: unknown) => {
    const translations = [...(formik.values.translations || [])]
    const index = translations.findIndex((t) => normLang(t.language) === lc)
    if (index >= 0) {
      translations[index] = {...translations[index], [field]: value}
    } else {
      translations.push({...emptySeoTranslation(selectedLanguage), [field]: value})
    }
    formik.setFieldValue('translations', translations)
  }

  const currentTranslation = getCurrentTranslation()
  if (!currentTranslation) return null

  return (
    <div className='card card-flush py-4 mb-4'>
      <div className='card-header'>
        <div className='card-title'>
          <h2>{intl.formatMessage({id: 'TOUR.FIELDS.SEO', defaultMessage: 'SEO'})}</h2>
        </div>
      </div>
      <div className='card-body pt-0'>
        <div className='row mb-4'>
          <div className='col-md-6'>
            <label className='form-label fw-semibold'>
              {intl.formatMessage({id: 'COLUMNS.SEO_SLUG', defaultMessage: 'URL slug (SEO)'})}
            </label>
            <input
              type='text'
              className='form-control'
              name='seoSlug'
              value={formik.values.seoSlug || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder='hotel-name-tunis'
            />
          </div>
        </div>

        <h3 className='mb-4'>Meta Tags</h3>
        <div className='row mb-4'>
          <div className='col-md-6 mb-3'>
            <label className='form-label fw-semibold'>
              {intl.formatMessage({id: 'COLUMNS.METE_TITLE'})}
            </label>
            <input
              type='text'
              className='form-control'
              value={currentTranslation.metaTitle || ''}
              onChange={(e) => updateTranslation('metaTitle', e.target.value)}
            />
          </div>
          <div className='col-md-6 mb-3'>
            <label className='form-label fw-semibold'>
              {intl.formatMessage({id: 'COLUMNS.METE_DESCRIPTION'})}
            </label>
            <textarea
              className='form-control'
              value={currentTranslation.metaDescription || ''}
              onChange={(e) => updateTranslation('metaDescription', e.target.value)}
              rows={2}
            />
          </div>
        </div>

        <div className='mb-6'>
          <label className='form-label fw-semibold'>
            {intl.formatMessage({id: 'COLUMNS.META_KEYWORDS'})}
          </label>
          <TagsComponent
            initialValues={currentTranslation.metaKeywords || []}
            onValuesChange={(updatedMetaKeywords) => {
              updateTranslation('metaKeywords', updatedMetaKeywords)
            }}
            type='metaKeywords'
            selectedLanguage={selectedLanguage}
          />
        </div>

        <h3 className='mb-4'>{intl.formatMessage({id: 'COLUMNS.OPEN_GRAPHE_TITLE'})} / Open Graph</h3>
        <div className='row mb-4'>
          <div className='col-md-6 mb-3'>
            <label className='form-label fw-semibold'>
              {intl.formatMessage({id: 'COLUMNS.OPEN_GRAPHE_TITLE'})}
            </label>
            <input
              type='text'
              className='form-control'
              value={currentTranslation.openGraphTitle || ''}
              onChange={(e) => updateTranslation('openGraphTitle', e.target.value)}
            />
          </div>
          <div className='col-md-6 mb-3'>
            <label className='form-label fw-semibold'>
              {intl.formatMessage({id: 'COLUMNS.OPEN_GRAPHE_IMAGE'})}
            </label>
            <input
              type='text'
              className='form-control'
              value={currentTranslation.openGraphImage || ''}
              onChange={(e) => updateTranslation('openGraphImage', e.target.value)}
            />
          </div>
          <div className='col-md-6 mb-3'>
            <label className='form-label fw-semibold'>
              {intl.formatMessage({id: 'COLUMNS.OPEN_GRAPHE_TYPE'})}
            </label>
            <select
              className='form-select'
              value={currentTranslation.openGraphType || 'website'}
              onChange={(e) => updateTranslation('openGraphType', e.target.value)}
            >
              <option value='website'>website</option>
              <option value='article'>article</option>
              <option value='product'>product</option>
            </select>
          </div>
          <div className='col-12 mb-3'>
            <label className='form-label fw-semibold'>
              {intl.formatMessage({id: 'COLUMNS.OPEN_GRAPHE_DESCRIPTION'})}
            </label>
            <textarea
              className='form-control'
              value={currentTranslation.openGraphDescription || ''}
              onChange={(e) => updateTranslation('openGraphDescription', e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <h3 className='mb-4'>Twitter</h3>
        <div className='row mb-4'>
          <div className='col-md-6 mb-3'>
            <label className='form-label fw-semibold'>
              {intl.formatMessage({id: 'COLUMNS.TWITTER_TITLE'})}
            </label>
            <input
              type='text'
              className='form-control'
              value={currentTranslation.twitterTitle || ''}
              onChange={(e) => updateTranslation('twitterTitle', e.target.value)}
            />
          </div>
          <div className='col-md-6 mb-3'>
            <label className='form-label fw-semibold'>
              {intl.formatMessage({id: 'COLUMNS.TWITTER_IMAGE'})}
            </label>
            <input
              type='text'
              className='form-control'
              value={currentTranslation.twitterImage || ''}
              onChange={(e) => updateTranslation('twitterImage', e.target.value)}
            />
          </div>
          <div className='col-12 mb-3'>
            <label className='form-label fw-semibold'>
              {intl.formatMessage({id: 'COLUMNS.TWITTER_DESCRIPTION'})}
            </label>
            <textarea
              className='form-control'
              value={currentTranslation.twitterDescription || ''}
              onChange={(e) => updateTranslation('twitterDescription', e.target.value)}
              rows={3}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export {HotelSeoTab}
